import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { activateOnKey } from "../../Helper/a11y";
import {
  DropdownPlacement,
  getHorizontalClipBounds,
  resolveDropdownPlacement,
} from "../../Helper/DropdownPlacement";
import { focusWithoutScroll } from "../../Helper/ScrollHelper";
import "./FilterSelect.scss";

export interface FilterSelectOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  ariaLabel: string;
  options: FilterSelectOption[];
  /** Undefined while the option list is still loading — renders an empty label. */
  value?: string;
  onChange: (value: string) => void;
  /** Icon or dot rendered before the selected label. */
  leading?: ReactNode;
}

/**
 * A dropdown rendered inside the widget instead of a native `<select>`.
 *
 * #143124: a native select hands the option list to the platform, and Android's
 * WebView draws it as the system radio-button dialog — an unstyled, distinctly
 * dated sheet that ignores the widget's theme entirely. Owning the popover
 * keeps the list looking the same on every platform.
 *
 * The panel is sized to its content rather than to the trigger, so translated
 * labels ("Meiste Stimmen" in a half-width trigger) stay readable; where it
 * hangs off the trigger is decided per open, see Helper/DropdownPlacement.
 */
export const FilterSelect = ({
  ariaLabel,
  options,
  value,
  onChange,
  leading,
}: FilterSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<DropdownPlacement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const close = useCallback((restoreFocus: boolean) => {
    setIsOpen(false);
    if (restoreFocus) {
      // Focus without preventScroll would scroll the host page (cross-frame
      // focus scroll on Safari) — same reason EmailUpvoteDialog uses it.
      focusWithoutScroll(triggerRef.current);
    }
  }, []);

  // Measured before paint, on the first render of the (still unclamped) panel:
  // that pass is the only chance to read the content's natural width.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return;
    }

    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    setPlacement(
      resolveDropdownPlacement({
        triggerLeft: triggerRect.left,
        triggerRight: triggerRect.right,
        contentWidth: panel.getBoundingClientRect().width,
        bounds: getHorizontalClipBounds(containerRef.current),
      }),
    );
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  const selectOption = (optionValue: string) => {
    if (optionValue !== value) {
      onChange(optionValue);
    }
    close(true);
  };

  const toggle = () => setIsOpen((wasOpen) => !wasOpen);

  return (
    <div
      ref={containerRef}
      className={`filter-select ${isOpen ? "filter-select--open" : ""}`}
    >
      <div
        ref={triggerRef}
        className="filter-select-trigger"
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={toggle}
        onKeyDown={activateOnKey(toggle)}
      >
        {leading}
        <span className="filter-select-value">{selectedOption?.label ?? ""}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="filter-select-caret"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {isOpen && (
        <div
          ref={panelRef}
          className={`filter-select-panel ${
            placement?.align === "end" ? "filter-select-panel--end" : ""
          }`}
          style={placement ? { maxWidth: `${placement.maxWidth}px` } : undefined}
          role="listbox"
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <div
                key={option.value}
                className={`filter-select-option ${isSelected ? "filter-select-option--selected" : ""}`}
                role="option"
                tabIndex={0}
                aria-selected={isSelected}
                onClick={() => selectOption(option.value)}
                onKeyDown={activateOnKey(() => selectOption(option.value))}
              >
                <span className="filter-select-option-label">{option.label}</span>
                {isSelected && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="filter-select-option-check"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
